@if (loadFailed()) {
  <div class="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
    <i class="pi pi-times-circle text-red-600 mt-0.5"></i>
    <span class="flex-1 text-sm text-red-900">Could not load this appointment.</span>
    <p-button label="Retry" icon="pi pi-refresh" size="small" severity="danger" [text]="true" (onClick)="retryLoad()" />
  </div>
} @else if (isLoadingAppointment()) {
  <div class="flex flex-col gap-4 pt-2.5" role="status" aria-busy="true">
    <p-skeleton height="2.5rem" />
    <p-skeleton height="2.5rem" />
    <p-skeleton height="10rem" />
  </div>
} @else {
  <div class="flex flex-col gap-7 pt-2.5">
    <p-floatlabel variant="on">
      <p-select
        inputId="appointment-contract"
        class="w-full"
        [options]="dealOptions()"
        optionLabel="buyerName"
        optionValue="dealId"
        [filter]="true"
        filterBy="buyerName,projectName,lotNo,modelName,elevationCode"
        filterPlaceholder="Search..."
        [showClear]="!isEdit()"
        [disabled]="isEdit()"
        [loading]="isLoadingDeals()"
        [virtualScroll]="dealOptions().length > 50"
        [virtualScrollItemSize]="56"
        appendTo="body"
        [(ngModel)]="dealId"
      >
        <ng-template #selectedItem let-deal>
          <span class="truncate">
            {{ deal.buyerName }}
            <span class="text-surface-500">(Lot {{ deal.lotNo }} · {{ deal.projectName }})</span>
          </span>
        </ng-template>
        <ng-template #item let-deal>
          <div class="flex min-w-0 flex-col gap-0.5">
            <span class="truncate text-surface-900">{{ deal.buyerName }}</span>
            <span class="flex flex-wrap items-center gap-x-1.5 text-xs text-surface-500">
              <span><span class="font-semibold text-surface-400">Project:</span> {{ deal.projectName }}</span>
              <span aria-hidden="true">·</span>
              <span><span class="font-semibold text-surface-400">Lot:</span> {{ deal.lotNo }}</span>
              @if (deal.modelName) {
                <span aria-hidden="true">·</span>
                <span>
                  <span class="font-semibold text-surface-400">Model/Elev:</span> {{ deal.modelName }}
                  @if (deal.elevationCode) {
                    <b>/</b> {{ deal.elevationCode }}
                  }
                </span>
              }
            </span>
          </div>
        </ng-template>
      </p-select>
      <label for="appointment-contract">Contract <span class="text-red-500">*</span></label>
    </p-floatlabel>

    <p-floatlabel variant="on">
      <p-multiselect
        inputId="appointment-steps"
        class="w-full"
        [options]="stepGroups()"
        [group]="true"
        optionGroupLabel="label"
        optionGroupChildren="items"
        optionLabel="label"
        optionValue="value"
        [filter]="true"
        [loading]="isLoadingSteps()"
        [disabled]="!deal()"
        appendTo="body"
        [(ngModel)]="stepIds"
      />
      <label for="appointment-steps">Appointment Steps</label>
    </p-floatlabel>

    <!-- <p-floatlabel variant="on">
      <generic-dropdown [placeholder]="''" [items]="typeItems" [allowSearch]="false" [(ngModel)]="appointmentTypeId" />
      <label for="appointment-type">Appointment Type</label>
    </p-floatlabel> -->

    <div class="flex flex-col gap-4">
      <p-floatlabel variant="on">
        <generic-dropdown
          [placeholder]="''"
          [items]="consultantItems()"
          [allowSearch]="true"
          [loading]="isLoadingConsultants()"
          [disabled]="!deal()"
          [(ngModel)]="consultantId"
        />
        <label for="appointment-consultant">Décor Consultant <span class="text-red-500">*</span></label>
      </p-floatlabel>

      @if (!consultantId()) {
        <span class="pl-4 text-[13px] leading-[18px] text-surface-500"
          >Choose a Décor Consultant to see what's already booked.</span
        >
      } @else {
        <div class="flex flex-col gap-5 -mt-2 p-4 rounded-2xl border border-surface-200 bg-white shadow-module">
          <div class="flex items-center gap-2.5 pb-4 border-b border-surface-200">
            <span
              class="flex shrink-0 items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white text-body-xs-600"
              aria-hidden="true"
              >{{ consultantInitials() }}</span
            >
            <div class="flex flex-col min-w-0">
              <span class="text-body-sm-600 text-surface-800 truncate">{{ consultantFirstName() }}'s Calendar</span>
              <span class="text-body-xs text-surface-600">Book into a free time on their calendar</span>
            </div>
          </div>

          <div class="flex flex-col gap-2.5">
            <div class="flex items-center justify-between gap-2">
              <span class="text-body-sm-600 text-surface-800">1. Pick a date</span>
              <div class="flex items-center gap-0.5">
                <p-button
                  icon="pi pi-chevron-left"
                  size="small"
                  severity="secondary"
                  ariaLabel="Previous week"
                  [text]="true"
                  [disabled]="isPrevWeekDisabled()"
                  (onClick)="shiftWeek(-1)"
                />
                <span class="min-w-[150px] text-center text-[13px] leading-[18px] font-medium text-surface-600"
                  >{{ weekStart() | date: 'MMM d' }} – {{ weekEnd() | date: 'MMM d, y' }}</span
                >
                <p-button
                  icon="pi pi-chevron-right"
                  size="small"
                  severity="secondary"
                  ariaLabel="Next week"
                  [text]="true"
                  (onClick)="shiftWeek(1)"
                />
              </div>
            </div>
            <div role="radiogroup" aria-label="Date" class="grid grid-cols-7 gap-1.5">
              @for (weekDay of weekDays(); track weekDay.date.getTime()) {
                <button
                  type="button"
                  role="radio"
                  class="flex flex-col items-center gap-0.5 px-0.5 py-2 rounded-xl border-0 cursor-pointer font-sans transition-shadow duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-default disabled:opacity-40"
                  [class]="
                    weekDay.isSelected
                      ? 'bg-amber-soft text-primary-500 shadow-outline-amber'
                      : 'bg-transparent text-surface-700 shadow-outline-surface enabled:hover:shadow-outline-amber'
                  "
                  [attr.aria-checked]="weekDay.isSelected"
                  [disabled]="weekDay.isPast"
                  (click)="pickDay(weekDay)"
                >
                  <span class="text-body-2xs-600 tracking-wider">{{ weekDay.date | date: 'EEE' | uppercase }}</span>
                  <span class="text-body-md-600 leading-snug">{{ weekDay.date | date: 'd' }}</span>
                  <span
                    class="text-body-2xs"
                    [class]="
                      weekDay.isSelected
                        ? 'text-primary-500'
                        : weekDay.bookedCount
                          ? 'text-surface-600'
                          : 'text-surface-500'
                    "
                  >
                    @if (weekDay.isPast) {
                      —
                    } @else if (weekDay.bookedCount) {
                      {{ weekDay.bookedCount }} booked
                    } @else {
                      Free
                    }
                  </span>
                </button>
              }
            </div>
          </div>

          <div class="flex flex-col gap-2.5">
            <div class="flex flex-col gap-0.5">
              <span class="text-body-sm-600 text-surface-800">2. Set the appointment time</span>
              <span class="text-[13px] leading-[18px] text-surface-600"
                >Any time of day works. Avoid the times already booked below.</span
              >
            </div>
            <div class="grid grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)] items-center gap-2 pt-2">
              <p-floatlabel variant="on">
                <generic-dropdown
                  [placeholder]="''"
                  [items]="startItems()"
                  [allowSearch]="true"
                  [(ngModel)]="startId"
                />
                <label for="appointment-start">Starts at <span class="text-red-500">*</span></label>
              </p-floatlabel>
              <span class="grid place-items-center text-surface-400"><i class="pi pi-arrow-right text-sm"></i></span>
              <p-floatlabel variant="on">
                <generic-dropdown
                  [placeholder]="''"
                  [items]="endItems()"
                  [allowSearch]="true"
                  [disabled]="startId() === null"
                  [(ngModel)]="endId"
                />
                <label for="appointment-end">Ends at <span class="text-red-500">*</span></label>
              </p-floatlabel>
            </div>
          </div>

          <div class="flex flex-col gap-2.5">
            <div class="flex items-baseline justify-between gap-2">
              <span class="text-body-sm-600 text-surface-800">
                @if (consultantFirstName(); as firstName) {
                  {{ firstName }}'s schedule ·
                }
                {{ day() | date: 'EEE, MMM d' }}
              </span>
              <span class="flex items-center gap-1.5 text-body-xs text-surface-500">
                @if (isLoadingWeek()) {
                  <i class="pi pi-spin pi-spinner text-[11px]"></i>
                }
                @if (bookedCount()) {
                  {{ bookedCount() }} booked
                }
              </span>
            </div>
            @if (!bookedCount() && !isLoadingWeek()) {
              <div class="flex items-center gap-2 px-3.5 py-3 rounded-xl bg-surface-50 text-body-xs text-surface-600">
                <i class="pi pi-check text-xs text-green-600"></i>Nothing booked. The whole day is open.
              </div>
            }
            <div class="flex flex-col gap-1.5">
              @for (row of agendaRows(); track row.key) {
                <div
                  class="grid grid-cols-agenda items-center gap-4 h-[38px] px-3.5 py-2.5 rounded-lg"
                  [class]="agendaClasses[row.kind].row"
                >
                  <span class="text-body-sm-600 tabular-nums whitespace-nowrap" [class]="agendaClasses[row.kind].time"
                    >{{ row.start | date: 'h:mm a' }} – {{ row.end | date: 'h:mm a' }}</span
                  >
                  @if (row.appointment; as appointment) {
                    <span class="min-w-0 truncate text-body-xs" [class]="agendaClasses[row.kind].text">
                      @if (appointment.steps.length) {
                        {{ appointment.steps[0].name }} ·
                      }
                      with {{ appointment.buyerName }}
                      @if (appointment.assignedUserId !== consultantId()) {
                        · with {{ appointment.assignedUserName }}
                      }
                    </span>
                    <p-tag value="BOOKED" severity="info" styleClass="!h-[18px] !py-0" />
                  } @else {
                    <span class="min-w-0 truncate text-body-xs" [class]="agendaClasses[row.kind].text"
                      >{{ deal()?.buyerName }} · {{ slot()?.durationMinutes }} min</span
                    >
                    @if (row.kind === agendaKind.Overlap) {
                      <p-tag value="OVERLAP" severity="danger" styleClass="!h-[18px] !py-0" />
                    } @else {
                      <p-tag value="NEW" severity="warn" styleClass="!h-[18px] !py-0" />
                    }
                  }
                </div>
              }
            </div>
            @if (isPastSlot()) {
              <span class="flex items-center gap-1.5 text-body-xs-500 text-red-500" role="alert">
                <i class="pi pi-info-circle text-xs"></i>This time has already passed. Pick a later start time.
              </span>
            } @else if (clash(); as clash) {
              <span class="flex items-center gap-1.5 text-body-xs-500 text-red-500" role="alert">
                <i class="pi pi-info-circle text-xs"></i>
                @if (isConsultantClash()) {
                  This overlaps {{ consultantFirstName() }}'s {{ clash.start | date: 'h:mm a' }} –
                  {{ clash.end | date: 'h:mm a' }} booking with {{ clash.appointment.buyerName }}. Change the start or
                  end time.
                } @else {
                  Lot {{ clash.appointment.lotNo }} already has a {{ clash.start | date: 'h:mm a' }} –
                  {{ clash.end | date: 'h:mm a' }} booking with {{ clash.appointment.assignedUserName }}. Change the
                  start or end time.
                }
              </span>
            }
          </div>
        </div>
      }
    </div>

    <div class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
      <p-floatlabel variant="on">
        <generic-dropdown
          [placeholder]="''"
          [items]="locationItems()"
          [allowSearch]="true"
          [loading]="isLoadingLocations()"
          [(ngModel)]="locationId"
        />
        <label for="appointment-location">Address <span class="text-red-500">*</span></label>
      </p-floatlabel>
      <p-button
        label="New Address"
        icon="pi pi-plus"
        [outlined]="true"
        styleClass="!h-10"
        (onClick)="openLocationDialog()"
      />
    </div>

    @if (isNotesOpen()) {
      <p-floatlabel variant="on">
        <textarea
          pTextarea
          id="appointment-notes"
          class="w-full"
          rows="2"
          [autoResize]="true"
          [maxlength]="maxNotesLength"
          [(ngModel)]="notes"
        ></textarea>
        <label for="appointment-notes">Notes</label>
      </p-floatlabel>
    } @else {
      <button
        type="button"
        class="flex items-center gap-1.5 self-start p-0 border-0 bg-transparent font-sans text-body-sm-500 text-primary-500 cursor-pointer rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
        (click)="notesOpened.set(true)"
      >
        <i class="pi pi-plus text-xs"></i
        ><span class="underline decoration-1 underline-offset-4 decoration-amber-500">Add a note</span>
      </button>
    }

    <div class="flex flex-wrap items-center justify-between gap-3 w-full pt-4 border-t border-surface-200">
      <div class="flex gap-2 ml-auto">
        <p-button
          label="Cancel"
          severity="secondary"
          [outlined]="true"
          styleClass="!h-10"
          (onClick)="cancelled.emit()"
        />
        <p-button
          [label]="submitLabel()"
          [disabled]="!canSave()"
          [loading]="isSaving()"
          styleClass="!h-10"
          (onClick)="save()"
        />
      </div>
    </div>
  </div>
}
<p-dialog
  header="Add New Address"
  [visible]="locationDialogVisible()"
  (visibleChange)="locationDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(92vw, 30rem)' }"
  appendTo="body"
>
  <div class="flex flex-col gap-4 pt-1">
    <span class="text-[13px] leading-[18px] text-surface-600"
      >Saved to the global address list and available for all appointments.</span
    >
    <app-addresses-primeng
      #address
      [fieldConfig]="locationFields"
      [submitted]="locationSubmitted()"
      [formControl]="locationControl"
    />
    <div class="flex justify-end gap-2">
      <p-button label="Cancel" severity="secondary" (onClick)="locationDialogVisible.set(false)" />
      <p-button label="Save Address" [loading]="isSavingLocation()" (onClick)="saveLocation(address)" />
    </div>
  </div>
</p-dialog>
